// Kid chat — the locked-down LLM chat surface inside the KidApp.
//
// Server-side (run.start with scope "kid") enforces the parent controls,
// the small self-scoped tool set, and the kid-safe prompt; this view
// stays intentionally plain by intent: a friendly bubble list + a
// single composer. No tool-call panels, reasoning, skills, or thread
// switching — just talk.
//
// What v2 polish adds:
//   - A "buddy" avatar tile next to each assistant bubble so the
//     conversation has a face, not just walls of text.
//   - Animated bouncing-dot typing indicator (CSS keyframes) instead
//     of a static "...".
//   - A bigger empty state with tappable starter chips — gives a kid
//     an opening when they don't know what to type yet.
//   - Subtle entrance animation on each new bubble + a fixed-height
//     composer with a clearer Send affordance.

import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../lib/rpc";
import { useChat } from "../../lib/use-chat";
import type { Me } from "../../lib/use-me";

interface KidChatProps {
  me: Me;
  notify: (msg: string) => void;
}

const LOCKED_COPY: Record<string, { emoji: string; line: string }> = {
  disabled: { emoji: "🔒", line: "Chat isn't turned on yet. Ask a grown-up to switch it on." },
  quiet_hours: { emoji: "🌙", line: "Chat's asleep right now. Try again later!" },
  limit_reached: { emoji: "😴", line: "That's all the chatting for today. Come back tomorrow!" },
  not_kid: { emoji: "🔒", line: "Chat isn't available here." },
};

// Starter chips for the empty state — a few easy entries kids can tap
// instead of typing. Kept short + concrete; mix of homework / fun /
// self-help so anyone finds something.
const STARTERS: { emoji: string; text: string }[] = [
  { emoji: "✏️", text: "Help me with my homework" },
  { emoji: "😂", text: "Tell me a joke" },
  { emoji: "📖", text: "Read me a short story" },
  { emoji: "🧠", text: "Ask me a fun riddle" },
];

export function KidChatView({ me, notify }: KidChatProps) {
  const chat = useChat();
  const [status, setStatus] = useState<{ allowed: boolean; reason: string | null } | null>(null);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.kidchatStatus().then(setStatus).catch(() => setStatus({ allowed: false, reason: "disabled" }));
  }, []);

  // Start a fresh thread once chat is allowed and none is active, so the
  // first send has somewhere to go.
  useEffect(() => {
    if (status?.allowed && !chat.active && !chat.loadingThreads) chat.newThread();
  }, [status?.allowed, chat.active, chat.loadingThreads]);

  // Keep pinned to the newest message as the conversation grows / streams.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.items.length, chat.thinking]);

  // Surface a run-level refusal (e.g. limit hit mid-session) and refresh the
  // gate so the composer locks.
  useEffect(() => {
    if (chat.error) { notify(chat.error); api.kidchatStatus().then(setStatus).catch(() => {}); }
  }, [chat.error]);

  const sendText = (text: string) => {
    const t = text.trim();
    if (!t || chat.sending || chat.thinking) return;
    setDraft("");
    chat.send(t, "default");
  };

  if (status === null) return <div className="kid-chat-loading">loading…</div>;

  if (!status.allowed) {
    const c = LOCKED_COPY[status.reason || "disabled"] || LOCKED_COPY.disabled;
    return (
      <div className="kid-chat-locked">
        <div className="kid-chat-locked-emoji">{c.emoji}</div>
        <div className="kid-chat-locked-line">{c.line}</div>
      </div>
    );
  }

  // Persisted items (post-agent:done) plus the live tail. The adult
  // chat does the same split — `items` is the server-truth rebuild
  // that lands when a run completes, and `messages.slice(persistedCount)`
  // is the optimistically-added user message (and, for adults, the
  // streaming assistant tokens). Kid scope suppresses token streaming
  // so the live tail here is normally just the user message; without
  // this dual render, the kid's own message wouldn't appear in the
  // bubble until the round-trip finished — which felt like a hang.
  const items = chat.items as Extract<typeof chat.items[number], { kind: "msg" }>[];
  const persistedMsgs = items.filter((it) => it.kind === "msg");
  const liveTail = chat.messages.slice(persistedMsgs.length);
  const bubbles = persistedMsgs as Extract<typeof chat.items[number], { kind: "msg" }>[];

  return (
    <div className="kid-chat">
      {/* Soft "who you're talking to" strip. Mascot avatar + name. Not
          a full header — just a touch of identity so the chat doesn't
          feel like talking to an empty void. */}
      <div className="kid-chat-banner">
        <div className="kid-chat-banner-avatar">🦊</div>
        <div className="kid-chat-banner-body">
          <div className="kid-chat-banner-name">Hub</div>
          <div className="kid-chat-banner-sub">your helper</div>
        </div>
      </div>

      <div className="kid-chat-scroll" ref={scrollRef}>
        {bubbles.length === 0 && !chat.thinking && (
          <div className="kid-chat-hello">
            <div className="kid-chat-hello-emoji">{me.avatarEmoji || "💬"}</div>
            <div className="kid-chat-hello-line">
              Hi {me.displayName}! What's on your mind?
            </div>
            <div className="kid-chat-starters">
              {STARTERS.map((s) => (
                <button
                  key={s.text}
                  type="button"
                  className="kid-chat-starter"
                  onClick={() => sendText(s.text)}
                  disabled={chat.sending || chat.thinking}
                >
                  <span className="kid-chat-starter-emoji">{s.emoji}</span>
                  <span className="kid-chat-starter-text">{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {bubbles.map((it) => {
          const isAssistant = it.msg.role === "assistant";
          // The model occasionally streams a leading "\n" or "\n\n"
          // before its actual reply (especially after tool-call gaps).
          // With white-space: pre-wrap that renders as visible blank
          // lines at the top of the bubble — looks like a layout bug.
          // Trim leading whitespace at render time; the bubble still
          // honors internal newlines via pre-wrap.
          const text = (it.msg.text || "").replace(/^\s+/, "");
          return (
            <div key={it.msg.id} className={`kid-bubble-row kid-bubble-row-${it.msg.role}`}>
              {isAssistant && <div className="kid-bubble-avatar">🦊</div>}
              <div className={`kid-bubble kid-bubble-${it.msg.role}`}>
                {text}
              </div>
            </div>
          );
        })}
        {/* Live tail — the user's just-sent message (and, for adults,
            the streaming assistant tokens). For kids we always have
            either zero or one live entry here: the optimistically-
            added user bubble that appears the instant Send is tapped,
            so the kid sees their own words right away even though
            assistant tokens are suppressed. The persisted version
            takes over on agent:done. */}
        {liveTail.map((m) => (
          <div key={m.id} className={`kid-bubble-row kid-bubble-row-${m.role}`}>
            {m.role === "assistant" && <div className="kid-bubble-avatar">🦊</div>}
            <div className={`kid-bubble kid-bubble-${m.role}`}>
              {(m.text || "").replace(/^\s+/, "")}
            </div>
          </div>
        ))}
        {chat.thinking && (
          <div className="kid-bubble-row kid-bubble-row-assistant">
            <div className="kid-bubble-avatar">🦊</div>
            <div className="kid-bubble kid-bubble-assistant kid-bubble-thinking" aria-label="thinking">
              {/* Rainbow shimmer (same gradient + sweep as the adult
               *  chat's running tool-call status) so the "I'm thinking"
               *  cue matches across surfaces. */}
              <span className="kid-thinking-shimmer">thinking</span>
              <span className="kid-typing-dot" />
              <span className="kid-typing-dot" />
              <span className="kid-typing-dot" />
            </div>
          </div>
        )}
      </div>

      <div className="kid-chat-composer">
        <input
          className="kid-chat-input"
          placeholder="Type a message…"
          value={draft}
          onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendText(draft); } }}
          disabled={chat.sending}
          maxLength={2000}
        />
        <button
          type="button"
          className={`kid-chat-send${draft.trim() && !chat.sending && !chat.thinking ? " is-ready" : ""}`}
          onClick={() => sendText(draft)}
          disabled={!draft.trim() || chat.sending || chat.thinking}
          aria-label="Send"
        >
          ➤
        </button>
      </div>
    </div>
  );
}
